import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,

  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  LocalShipping as DispatchIcon,
  CheckCircle as DeliverIcon,
  Visibility as ViewIcon,
  DirectionsCar as InTransitIcon,
  Undo as ReturnIcon,
  Assignment as SalesOrderIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import { deliveryChallanService } from '../../../services/deliveryChallanService';
import { 
  DeliveryChallan, 
  DeliveryChallanStatus, 
  DELIVERY_CHALLAN_STATUS_COLORS,
  DELIVERY_CHALLAN_STATUS_LABELS
} from '../../../types/deliveryChallan';
import { formatCurrency } from '../../../utils/formatters';
import DocumentAuditHistory from '../../../components/audit/DocumentAuditHistory';
import { DocumentContextMenu } from '../../../components/documents/DocumentContextMenu';

const DeliveryChallanDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [deliveryChallan, setDeliveryChallan] = useState<DeliveryChallan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  // Status history would be populated from audit trail or backend
  const statusHistory: Array<{
    status: DeliveryChallanStatus;
    timestamp: string;
    admin_name?: string;
    notes?: string;
  }> = [];

  // Dialog states
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [dispatchDate, setDispatchDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<DeliveryChallanStatus>(DeliveryChallanStatus.DISPATCHED);
  const [statusNotes, setStatusNotes] = useState('');

  // Fetch delivery challan data
  useEffect(() => {
    const fetchDeliveryChallan = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await deliveryChallanService.getDeliveryChallan(id);
        setDeliveryChallan(data);
      } catch (error: any) {
        console.error('Failed to fetch delivery challan:', error);
        setError(error?.response?.data?.detail || 'Failed to load delivery challan');
      } finally {
        setLoading(false);
      }
    };

    fetchDeliveryChallan();
  }, [id]);

  const getStatusColor = useCallback((status: DeliveryChallanStatus) => {
    return DELIVERY_CHALLAN_STATUS_COLORS[status] || 'default';
  }, []);

  const permissions = useMemo(() => {
    if (!deliveryChallan) return {
      canEdit: false,
      canDispatch: false,
      canMarkInTransit: false,
      canDeliver: false,
      canMarkReturned: false
    };

    return {
      canEdit: deliveryChallan.status === DeliveryChallanStatus.DRAFT,
      canDispatch: deliveryChallan.status === DeliveryChallanStatus.DRAFT,
      canMarkInTransit: deliveryChallan.status === DeliveryChallanStatus.DISPATCHED,
      canDeliver: [
        DeliveryChallanStatus.DISPATCHED,
        DeliveryChallanStatus.IN_TRANSIT
      ].includes(deliveryChallan.status),
      canMarkReturned: deliveryChallan.status === DeliveryChallanStatus.IN_TRANSIT
    };
  }, [deliveryChallan?.status]);

  const handleEdit = () => {
    navigate(`/admin/documents/delivery-challans/${id}/edit`);
  };

  const handleDownloadPDF = async () => {
    if (!deliveryChallan) return;

    try {
      setActionLoading(true);
      const blob = await deliveryChallanService.downloadPDF(deliveryChallan.id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${deliveryChallan.challan_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to download PDF:', error);
      enqueueSnackbar('Failed to download PDF', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDispatch = async () => {
    if (!deliveryChallan) return;

    try {
      setActionLoading(true);
      const updatedChallan = await deliveryChallanService.markAsDispatched(deliveryChallan.id, dispatchDate);
      setDeliveryChallan(updatedChallan);
      setDispatchDialogOpen(false);
      enqueueSnackbar('Delivery challan marked as dispatched', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to dispatch delivery challan:', error);
      enqueueSnackbar('Failed to dispatch delivery challan', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeliver = async () => {
    if (!deliveryChallan) return;

    try {
      setActionLoading(true);
      const updatedChallan = await deliveryChallanService.markAsDelivered(deliveryChallan.id);
      setDeliveryChallan(updatedChallan);
      enqueueSnackbar('Delivery challan marked as delivered', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to mark as delivered:', error);
      enqueueSnackbar('Failed to mark as delivered', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkInTransit = async () => {
    if (!deliveryChallan) return;

    try {
      setActionLoading(true);
      const updatedChallan = await deliveryChallanService.updateStatus(deliveryChallan.id, {
        status: DeliveryChallanStatus.IN_TRANSIT
      });
      setDeliveryChallan(updatedChallan);
      enqueueSnackbar('Delivery challan marked as in transit', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to mark as in transit:', error);
      enqueueSnackbar('Failed to mark as in transit', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkReturned = async () => {
    if (!deliveryChallan) return;

    try {
      setActionLoading(true);
      const updatedChallan = await deliveryChallanService.updateStatus(deliveryChallan.id, {
        status: DeliveryChallanStatus.RETURNED
      });
      setDeliveryChallan(updatedChallan);
      enqueueSnackbar('Delivery challan marked as returned', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to mark as returned:', error);
      enqueueSnackbar('Failed to mark as returned', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!deliveryChallan) return;

    try {
      setActionLoading(true);
      const updatedChallan = await deliveryChallanService.updateStatus(deliveryChallan.id, {
        status: newStatus,
        notes: statusNotes
      });
      setDeliveryChallan(updatedChallan);
      setStatusUpdateOpen(false);
      setStatusNotes('');
      enqueueSnackbar('Status updated successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to update status:', error);
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewSalesOrder = () => {
    if (deliveryChallan?.sales_order_id) {
      navigate(`/admin/documents/sales-orders/${deliveryChallan.sales_order_id}`);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!deliveryChallan) {
    return (
      <Box>
        <Alert severity="error">Delivery challan not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/admin/dashboard">
          Dashboard
        </Link>
        <Link color="inherit" href="/admin/documents/delivery-challans">
          Delivery Challans
        </Link>
        <Typography color="text.primary">
          {deliveryChallan.challan_number}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/admin/documents/delivery-challans')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">
              Delivery Challan {deliveryChallan.challan_number}
            </Typography>
            <Box display="flex" alignItems="center" gap={2} mt={1}>
              <Chip
                label={DELIVERY_CHALLAN_STATUS_LABELS[deliveryChallan.status]}
                color={getStatusColor(deliveryChallan.status) as any}
                size="small"
              />
              {deliveryChallan.sales_order && (
                <Tooltip title="View sales order">
                  <Chip
                    label={`Order: ${deliveryChallan.sales_order.order_number}`}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={handleViewSalesOrder}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
        
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<ViewIcon />}
            onClick={handleDownloadPDF}
            disabled={actionLoading}
          >
            PDF Preview
          </Button>
          
          {permissions.canEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit
            </Button>
          )}
          
          {deliveryChallan.sales_order && (
            <Button
              variant="outlined"
              startIcon={<SalesOrderIcon />}
              onClick={handleViewSalesOrder}
            >
              View Sales Order
            </Button>
          )}
          
          {permissions.canDispatch && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<DispatchIcon />}
              onClick={() => setDispatchDialogOpen(true)}
              disabled={actionLoading}
            >
              Mark as Dispatched
            </Button>
          )}
          
          {permissions.canMarkInTransit && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<InTransitIcon />}
              onClick={handleMarkInTransit}
              disabled={actionLoading}
            >
              Mark In Transit
            </Button>
          )}
          
          {permissions.canDeliver && (
            <Button
              variant="contained"
              color="success"
              startIcon={<DeliverIcon />}
              onClick={handleDeliver}
              disabled={actionLoading}
            >
              Mark as Delivered
            </Button>
          )}
          
          {permissions.canMarkReturned && (
            <Button
              variant="contained"
              color="error"
              startIcon={<ReturnIcon />}
              onClick={handleMarkReturned}
              disabled={actionLoading}
            >
              Mark as Returned
            </Button>
          )}
          
          <DocumentContextMenu
            documentId={deliveryChallan.id}
            documentType="delivery_challan"
            documentNumber={deliveryChallan.challan_number}
            customerEmail={deliveryChallan.customer?.email}
            customerPhone={deliveryChallan.customer?.phone}
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Delivery Challan Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Challan Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Challan Number
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {deliveryChallan.challan_number}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Challan Date
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(deliveryChallan.challan_date), 'dd/MM/yyyy')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Dispatch Date
                  </Typography>
                  <Typography variant="body1">
                    {deliveryChallan.dispatch_date 
                      ? format(new Date(deliveryChallan.dispatch_date), 'dd/MM/yyyy')
                      : 'Not dispatched'
                    }
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={DELIVERY_CHALLAN_STATUS_LABELS[deliveryChallan.status]}
                      color={getStatusColor(deliveryChallan.status) as any}
                      size="small"
                    />
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setStatusUpdateOpen(true)}
                    >
                      Update
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Customer Information */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {deliveryChallan.customer?.business_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Contact: {deliveryChallan.customer?.contact_person}
                  </Typography>
                  {deliveryChallan.customer?.email && (
                    <Typography variant="body2" color="text.secondary">
                      Email: {deliveryChallan.customer.email}
                    </Typography>
                  )}
                  {deliveryChallan.customer?.phone && (
                    <Typography variant="body2" color="text.secondary">
                      Phone: {deliveryChallan.customer.phone}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Transport Details */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Transport Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Transporter
                  </Typography>
                  <Typography variant="body1">
                    {deliveryChallan.transporter_name || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Vehicle Number
                  </Typography>
                  <Typography variant="body1">
                    {deliveryChallan.vehicle_number || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    LR Number
                  </Typography>
                  <Typography variant="body1">
                    {deliveryChallan.lr_number || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    E-Way Bill Number
                  </Typography>
                  <Typography variant="body1">
                    {deliveryChallan.e_way_bill_number || 'Not specified'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Line Items
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Line Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deliveryChallan.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {item.variant?.product?.name} - {item.variant?.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              SKU: {item.variant?.sku}
                            </Typography>
                            {item.description && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {item.description}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(item.line_total || 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Notes */}
          {deliveryChallan.notes && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body2">
                  {deliveryChallan.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Summary and History */}
        <Grid item xs={12} md={4}>
          {/* Sales Order Summary */}
          {deliveryChallan.sales_order && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sales Order Summary
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Order Number:</Typography>
                  <Typography fontWeight={600}>
                    {deliveryChallan.sales_order.order_number}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Order Status:</Typography>
                  <Typography>
                    {deliveryChallan.sales_order.status.toUpperCase()}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Total Amount:</Typography>
                  <Typography fontWeight={600} color="primary">
                    {formatCurrency(deliveryChallan.sales_order.total_amount)}
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<SalesOrderIcon />}
                  onClick={handleViewSalesOrder}
                  sx={{ mt: 1 }}
                >
                  View Sales Order
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Challan History */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Challan History
              </Typography>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Created: {format(new Date(deliveryChallan.created_at), 'dd/MM/yyyy HH:mm')}
                </Typography>
                {deliveryChallan.created_by_admin && (
                  <Typography variant="body2" color="text.secondary">
                    By: {deliveryChallan.created_by_admin.name}
                  </Typography>
                )}
                {deliveryChallan.dispatch_date && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Dispatched: {format(new Date(deliveryChallan.dispatch_date), 'dd/MM/yyyy')}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Status History */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status History
              </Typography>
              <Box>
                {statusHistory.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {statusHistory.map((entry, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 1.5,
                          borderLeft: 3,
                          borderColor: DELIVERY_CHALLAN_STATUS_COLORS[entry.status] + '.main',
                          bgcolor: 'background.default',
                          borderRadius: 1
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Chip
                            label={DELIVERY_CHALLAN_STATUS_LABELS[entry.status]}
                            color={getStatusColor(entry.status) as any}
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(entry.timestamp), 'dd/MM/yyyy HH:mm')}
                          </Typography>
                        </Box>
                        {entry.admin_name && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            By: {entry.admin_name}
                          </Typography>
                        )}
                        {entry.notes && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Note: {entry.notes}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Current Status: {DELIVERY_CHALLAN_STATUS_LABELS[deliveryChallan.status]}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Audit History */}
        <Grid item xs={12}>
          <DocumentAuditHistory
            documentId={deliveryChallan.id}
            documentType="delivery_challan"
            title="Delivery Challan Audit History"
          />
        </Grid>
      </Grid>

      {/* Dispatch Dialog */}
      <Dialog open={dispatchDialogOpen} onClose={() => setDispatchDialogOpen(false)}>
        <DialogTitle>Mark as Dispatched</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will mark the delivery challan as dispatched.
          </Typography>
          <TextField
            fullWidth
            label="Dispatch Date"
            type="date"
            value={dispatchDate}
            onChange={(e) => setDispatchDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDispatchDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDispatch}
            disabled={actionLoading}
          >
            {actionLoading ? 'Dispatching...' : 'Mark as Dispatched'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusUpdateOpen} onClose={() => setStatusUpdateOpen(false)}>
        <DialogTitle>Update Challan Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>New Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value as DeliveryChallanStatus)}
              label="New Status"
            >
              {Object.values(DeliveryChallanStatus).map((status) => (
                <MenuItem key={status} value={status}>
                  {DELIVERY_CHALLAN_STATUS_LABELS[status]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={3}
            value={statusNotes}
            onChange={(e) => setStatusNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusUpdateOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleStatusUpdate}
            disabled={actionLoading}
          >
            {actionLoading ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeliveryChallanDetail;